/* eslint-disable @typescript-eslint/no-explicit-any */
import { format as formatDateFns, isValid as isValidDate } from 'date-fns';
import { FilterSearchDto } from '../store/Search';
import { ApiAdvancedFilter, FilterableField, OrderRequest } from '../store/GeneralStore';
import { BaseRunTotal, CustomerRunTotal, TicketRunTotal } from '../store/RunTotal';
import { currencyFields, percentFields } from '../components/Pages/Asset/assetHelpers';
import {
  provisionalCurrencyFields,
  provisionalPercentFields
} from '../components/Pages/ProvisionalAsset/provisionalAssetHelpers';
import { Asset } from '../store/Asset';
import { ProvisionalAsset } from '../store/ProvisionalAsset';
import { RunStatus } from '../store/Run';
import { CustomView, GridPreferences, PrefKey, UserPreferencesContent } from '../store/UserCustomizations';
import { Adjustment, adjustmentCurrencyFields } from '../store/Adjustment';
import { PaymentFrequency, booleanFields, doublewideFields } from './constants';

export function formatInfo(obj: string) {
  return <span style={{ whiteSpace: 'pre-wrap' }}>{obj}</span>;
}

export const getNestedValue = (obj: object, keyStr: string) => {
  const nestedVal = keyStr.split('.').reduce((o: object, i: string) => o?.[i as keyof typeof o], obj);
  return typeof nestedVal === 'string' ? nestedVal : '';
};

export const truncateStr = (val: string, maxLength = 20) =>
  val.length > maxLength ? `${val.substring(0, maxLength)}...` : val;

export const objectNotEmpty = (obj: object) => !!Object.keys(obj).length;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getObjValidity = (data: any, requiredKeys: string[]) => {
  const missingProperties = Object.keys(data)
    .filter((key) => requiredKeys.includes(key) && !data[key])
    .map((key) => key);

  return { errors: missingProperties, valid: missingProperties.length === 0 };
};

export const capitalizeText = (text: string): string => text[0].toUpperCase() + text.slice(1).toLowerCase();

export const isCurrency = (field: string) =>
  ['amount', 'revenueRecurringCorrection', 'revenueOneTimeCorrection'].includes(field) ||
  currencyFields.includes(field as keyof Asset) ||
  provisionalCurrencyFields.includes(field as keyof ProvisionalAsset) ||
  adjustmentCurrencyFields.includes(field as keyof Adjustment);
export const isPercent = (field: string) =>
  percentFields.includes(field as keyof Asset) || provisionalPercentFields.includes(field as keyof ProvisionalAsset);
export const isBoolean = (field: string) => booleanFields.includes(field);
export const isDoubleWide = (field: string) => doublewideFields.includes(field);

export const formatPrice = (value: number | null | undefined): string | null => {
  if (value === null || value === undefined) return null;
  if (value === 0) return '$0.00';

  const roundedValue = Math.round((value + Number.EPSILON) * 100) / 100;
  const formattedValue = roundedValue.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  return roundedValue < 0 ? `(${formattedValue.replace('-', '')})` : formattedValue;
};

export const formatPercent = (value: number | null | undefined, trimZeros = false): string => {
  if (value === null || value === undefined) return trimZeros ? '0%' : '0.00%';

  const rPercentage = parseFloat(value.toFixed(2));
  let formattedPercentage = rPercentage < 0 ? `(${Math.abs(rPercentage).toFixed(2)}%)` : `${rPercentage.toFixed(2)}%`;
  if (trimZeros) {
    formattedPercentage = formattedPercentage.replace(/\.00%$/, '%');
  }
  return formattedPercentage;
};

export const CHIP_ELIGIBLE_FIELDS = [
  'assetCommissionStatus',
  'commissionStatus',
  'commissionsStatus',
  'parentCommissionsStatus',
  'paymentFrequency',
  'paymentFrequencyOverride',
  'runStatus'
];

export const chipStyles = (label: string) => {
  const styles = getChipStylesByLabel(label);

  return {
    alignItems: 'center',
    backgroundColor: styles.bgColor,
    borderRadius: '4px',
    color: styles.labelColor,
    justifyContent: 'center',
    fontSize: '14px',
    verticalAlign: 'middle'
  };
};

const getChipStylesByLabel = (label: string) => {
  switch (label) {
    case 'None':
    case 'Cancelled':
    case 'Legacy Archive':
    case 'Non-Commissionable':
      return {
        labelColor: '#606060',
        bgColor: '#D1D6E5'
      };

    case 'Commissioning':
    case 'Partially Commissioning':
    case 'Commissioning - One Time':
      return {
        labelColor: '#FFFFFF',
        bgColor: '#00838F'
      };

    case 'Decommissioned':
      return {
        labelColor: '#FFFFFF',
        bgColor: '#A0A7BA'
      };

    case 'Pending Commissions':
      return {
        labelColor: '#FFFFFF',
        bgColor: '#00BCD4'
      };

    case RunStatus.DATE_ONLY:
      return {
        labelColor: '#606060',
        bgColor: '#D1D6E5'
      };

    case RunStatus.PROCESSING:
      return {
        labelColor: '#FFFFFF',
        bgColor: '#BCC429'
      };

    case RunStatus.PENDING_LOCK:
      return {
        labelColor: '#FFFFFF',
        bgColor: '#A3A042'
      };

    case RunStatus.LOCKED:
      return {
        labelColor: '#FFFFFF',
        bgColor: '#9CA3AF'
      };

    case PaymentFrequency.QUARTERLY:
      return {
        labelColor: '#606060',
        bgColor: '#B9F6CA'
      };

    case PaymentFrequency.ANNUALLY:
      return {
        labelColor: '#606060',
        bgColor: '#E2F595'
      };

    case PaymentFrequency.ONE_TIME:
      return {
        labelColor: '#606060',
        bgColor: '#FFE6BB'
      };

    case PaymentFrequency.MONTHLY:
      return {
        labelColor: '#606060',
        bgColor: '#E1F5FE'
      };

    default:
      return {
        labelColor: '#606060',
        bgColor: '#D1D6E5'
      };
  }
};

export const isMonthYearLabel = (label: string): boolean => {
  return [
    'First Run',
    'First Run Disputed',
    'Last Run Resolved',
    'First Commission Run',
    'Last Commission Run',
    'Expected First Commission Run',
    'Actual First Commission Run',
    'Actual First SPIFF Run',
    'Actual Decommission Run',
    'Expected Decommission Run'
  ].includes(label);
};

export const isMonthYearField = (field: string): boolean => {
  return [
    'firstRun',
    'lastRun',
    'firstCommissionRun',
    'lastCommissionRun',
    'expectedFirstCommissionRun',
    'actualFirstCommissionRun',
    'actualFirstSpiffRun',
    'actualDecommissionedDate',
    'expectedDecommissionDate'
  ].includes(field);
};

export const getMonthlyRecurringData = (
  runTotals: CustomerRunTotal[] | TicketRunTotal[],
  selectedRun: string | null
) => {
  if (!selectedRun) return [];

  let run: BaseRunTotal | undefined;
  if (runTotals.length > 0 && (runTotals[0] as TicketRunTotal).supplier_id) {
    run = (runTotals as TicketRunTotal[]).find((r) => r.rpm_run_id === selectedRun);
  } else {
    run = (runTotals as CustomerRunTotal[]).find((r) => r.rpm_run_id === selectedRun);
  }
  if (!run) return [];

  return [
    {
      id: 'MRC',
      expected: run.charge_recurring_expected,
      actual: run.charge_recurring_actual,
      difference: run.charge_recurring_difference
    },
    {
      id: 'MRR',
      expected: run.revenue_recurring_expected,
      actual: run.revenue_recurring_actual,
      difference: run.revenue_recurring_difference
    },
    {
      id: 'MRR % of MRC',
      expected: run.revenue_recurring_expected_percent,
      actual: run.revenue_recurring_actual_percent,
      difference: run.revenue_recurring_difference_percent
    },
    {
      id: 'True-Up',
      expected: run.revenue_trueup_expected,
      actual: run.revenue_trueup_actual,
      difference: run.revenue_trueup_difference
    }
  ];
};

export const getAllTimeOneTimeData = (runTotals: CustomerRunTotal[] | TicketRunTotal[]) => {
  if (!runTotals || runTotals.length === 0) return [];

  let typedRunTotals: BaseRunTotal[] = [];
  typedRunTotals = (runTotals[0] as TicketRunTotal).supplier_id
    ? (runTotals as TicketRunTotal[])
    : (runTotals as CustomerRunTotal[]);
  const charge_onetime_expected = typedRunTotals.reduce((s, r) => s + (r.charge_onetime_expected || 0), 0);
  const charge_onetime_actual = typedRunTotals.reduce((s, r) => s + (r.charge_onetime_actual || 0), 0);
  const charge_onetime_difference = typedRunTotals.reduce((s, r) => s + (r.charge_onetime_difference || 0), 0);
  const revenue_onetime_expected = typedRunTotals.reduce((s, r) => s + (r.revenue_onetime_expected || 0), 0);
  const revenue_onetime_actual = typedRunTotals.reduce((s, r) => s + (r.revenue_onetime_actual || 0), 0);
  const revenue_onetime_difference = typedRunTotals.reduce((s, r) => s + (r.revenue_onetime_difference || 0), 0);
  const revenue_spiff_expected = typedRunTotals.reduce((s, r) => s + (r.revenue_spiff_expected || 0), 0);
  const revenue_spiff_actual = typedRunTotals.reduce((s, r) => s + (r.revenue_spiff_actual || 0), 0);
  const revenue_spiff_difference = typedRunTotals.reduce((s, r) => s + (r.revenue_spiff_difference || 0), 0);
  const revenue_trueup_expected = typedRunTotals.reduce((s, r) => s + (r.revenue_trueup_expected || 0), 0);
  const revenue_trueup_actual = typedRunTotals.reduce((s, r) => s + (r.revenue_trueup_actual || 0), 0);
  const revenue_trueup_difference = typedRunTotals.reduce((s, r) => s + (r.revenue_trueup_difference || 0), 0);

  return [
    {
      id: 'One-Time Charge',
      expected: charge_onetime_expected,
      actual: charge_onetime_actual,
      difference: charge_onetime_difference
    },
    {
      id: 'One-Time Revenue',
      expected: revenue_onetime_expected,
      actual: revenue_onetime_actual,
      difference: revenue_onetime_difference
    },
    {
      id: 'SPIFF',
      expected: revenue_spiff_expected,
      actual: revenue_spiff_actual,
      difference: revenue_spiff_difference
    },
    {
      id: 'True-Up',
      expected: revenue_trueup_expected,
      actual: revenue_trueup_actual,
      difference: revenue_trueup_difference
    }
  ];
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const arrayMove = (arr: any[], from: number, to: number) => {
  const element = arr[from];
  arr.splice(from, 1);
  arr.splice(to, 0, element);
};

export const formatCamelCaseKey = (key: string) => {
  return key.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase());
};

export const buildAdvancedQueryURL = (filters: ApiAdvancedFilter, baseUrl: string): string => {
  let url = `${process.env.REACT_APP_API_SERVER_URL}/${baseUrl}?`;
  const urlParams = new URLSearchParams();

  for (const [key, value] of Object.entries(filters)) {
    // A spread of a caller's partial filters carries explicit undefined keys, so a present key is not a present value.
    if (value === undefined || value === null) continue;

    if (key === 'filters') {
      value.forEach((filter: FilterableField) => {
        const formattedValue = Array.isArray(filter.value) ? filter.value.join(',') : filter.value;
        urlParams.append(filter.field, encodeURIComponent(formattedValue));
        urlParams.append(`${filter.field}_operator`, filter.operator);
      });
    } else if (key === 'orders') {
      value.forEach((order: OrderRequest) => {
        urlParams.append('sort', order.sort);
        urlParams.append('direction', order.direction || 'desc');
      });
    } else urlParams.append(key, value.toString());
  }

  if (urlParams) url += urlParams.toString();

  return url;
};

export const buildQueryURL = (filters: FilterSearchDto, baseUrl: string): string => {
  let url = `${process.env.REACT_APP_API_SERVER_URL}/${baseUrl}?`;
  const urlParams = new URLSearchParams();

  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== null) urlParams.append(key, value.toString());
  }

  if (urlParams) url += urlParams.toString();

  return url;
};

export const getDate = (dateString: string) => {
  return new Date(dateString);
};

export const numberValueFormatter = (value: number | string | null | undefined): string => {
  if (value === null || value === undefined || value === '-') return '-';

  const numValue = Number(value);
  if (isNaN(numValue)) return String(value);

  return new Intl.NumberFormat('en-US').format(numValue);
};

// dayjs's full localizedFormat preset vocabulary (en locale), spelled out as date-fns
// tokens since date-fns has no equivalent named presets. Verified 1:1 against dayjs's
// actual output for every token, not just the ones currently called -- an untranslated
// preset wouldn't crash, it would silently format as something else entirely (date-fns
// assigns different meanings to bare 'L'/'LL'/'l'/'ll', e.g. "stand-alone month").
const LOCALIZED_FORMAT_PRESETS: Record<string, string> = {
  LT: 'h:mm a',
  LTS: 'h:mm:ss a',
  L: 'MM/dd/yyyy',
  LL: 'MMMM d, yyyy',
  LLL: 'MMMM d, yyyy h:mm a',
  LLLL: 'EEEE, MMMM d, yyyy h:mm a',
  l: 'M/d/yyyy',
  ll: 'MMM d, yyyy',
  lll: 'MMM d, yyyy h:mm a',
  llll: 'EEE, MMM d, yyyy h:mm a',
  // 'xxx' (not 'XXX'): dayjs's Z token always renders a numeric offset ('+00:00'), never the
  // bare 'Z' shorthand date-fns's uppercase X-family substitutes at zero offset.
  'lll (Z)': 'MMM d, yyyy h:mm a (xxx)'
};

// Translates the dayjs/Moment-style tokens this codebase's format strings are written in
// (YYYY, DD, A, ...) into date-fns's equivalent tokens (yyyy, dd, a, ...). date-fns
// deliberately throws on bare Moment tokens like `D`/`Y` to prevent exactly this kind of
// migration bug, so every format string must be translated before it reaches date-fns.
const toDateFnsFormat = (formatDate: string): string => {
  const preset = LOCALIZED_FORMAT_PRESETS[formatDate];
  if (preset) return preset;

  return formatDate.replace(/[A-Za-z]+/g, (token) => {
    if (token[0] === 'Y') return 'y'.repeat(token.length);
    if (token[0] === 'D') return 'd'.repeat(token.length);
    if (token[0] === 'A') return 'a';
    return token;
  });
};

// Change-history audit values (jsonb from_value/to_value) are written by a Postgres
// trigger via to_jsonb() straight off `timestamp without time zone` columns, so they
// arrive with no zone designator -- unlike every other endpoint, which round-trips
// through a JS Date and always gets a Z. dayjs.utc() used to read any bare datetime as
// UTC regardless; new Date() reads it as local, so it must be normalized before parsing.
const BARE_DATETIME = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(:\d{2})?(\.\d+)?$/;

const normalizeToUTC = (dateValue: string | Date | number): string | Date | number =>
  typeof dateValue === 'string' && BARE_DATETIME.test(dateValue) ? `${dateValue.replace(' ', 'T')}Z` : dateValue;

export const getFormattedDate = (
  dateValue: string | Date | number | undefined | null,
  formatDate = 'MMM D, YYYY',
  emptyMessage = ''
): string => {
  if (!dateValue) return emptyMessage;

  const parsedDate = new Date(normalizeToUTC(dateValue));
  if (!isValidDate(parsedDate)) return emptyMessage;

  const hasTime = /\b(H{1,2}|h{1,2}|k{1,2}|A|a|L{3,4}|l{3,4}|LT|LTS)\b/.test(formatDate);
  // Detect format with time. https://day.js.org/docs/en/display/format
  // Date-only formats render the UTC calendar date, unaffected by the viewer's local
  // timezone; formats with a time component render in the viewer's local timezone.
  // date-fns always formats using local getters, so date-only formats are rebuilt from
  // parsedDate's UTC fields read as local fields -- via the local Date constructor,
  // not an offset shift, since a shift computed at the original instant can fall on the
  // other side of a DST transition from the fields it's read back at.
  const value = hasTime
    ? parsedDate
    : new Date(
        parsedDate.getUTCFullYear(),
        parsedDate.getUTCMonth(),
        parsedDate.getUTCDate(),
        parsedDate.getUTCHours(),
        parsedDate.getUTCMinutes(),
        parsedDate.getUTCSeconds(),
        parsedDate.getUTCMilliseconds()
      );

  return formatDateFns(value, toDateFnsFormat(formatDate));
};

export const camelToKebab = (str: string): string => {
  return str.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();
};

// monthOffset shifts the target month before pinning the day to 15, e.g. getUTCRunDate(x, 1)
// for "the run date of the month after x".
//
// A Date argument is read via local getters -- it's a fresh local calendar pick straight from a
// date picker's onChange, and an unset day-of-month there defaults to the 1st, which local
// midnight in any timezone ahead of UTC converts to the previous day/month if read via UTC
// getters instead. A string (ISO) argument is read via UTC getters, since it's an already-anchored
// stored value and should show the same calendar month to every viewer regardless of their own
// timezone.
export const getUTCRunDate = (date: string | Date, monthOffset = 0) => {
  const d = new Date(date);
  const [year, month] =
    typeof date === 'string' ? [d.getUTCFullYear(), d.getUTCMonth()] : [d.getFullYear(), d.getMonth()];
  return new Date(Date.UTC(year, month + monthOffset, 15));
};

// Local getters on purpose, unlike getUTCRunDate above: the caller passes a 'Mon YYYY' label, not an ISO instant.
export const getUTCStartAndEndMonthDates = (date: string) => {
  const currentDate = new Date(date);
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const firstDayOfMonth = Date.UTC(year, month, 1);
  const lastDayOfMonth = Date.UTC(year, month + 1, 0);

  return [new Date(firstDayOfMonth), new Date(lastDayOfMonth)];
};

// Same string-vs-Date distinction as getUTCRunDate above; noon anchor is only ~12h from a day boundary, so a
// stored Date routed through the local-getter branch could shift a day in UTC+13/+14 -- fine while every caller
// only passes a fresh "now" value.
export const getUTCDate = (date: string | Date, offset = 12) => {
  const d = new Date(date);
  const [year, month, day] =
    typeof date === 'string'
      ? [d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()]
      : [d.getFullYear(), d.getMonth(), d.getDate()];
  const startOfUtcDay = Date.UTC(year, month, day);
  return new Date(startOfUtcDay + offset * 60 * 60 * 1000);
};

export const convertToLocalDate = (dateValue: string | Date | number | undefined | null) => {
  if (!dateValue) return null;

  const d = new Date(dateValue);
  if (!isValidDate(d)) return null;

  return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
};

export const isObjectEmpty = (obj: object): boolean => {
  return Object.keys(obj).length === 0;
};

export const getDecimal = (num: number): number => {
  return Number(num.toFixed(2));
};

export const getTabs = (obj: UserPreferencesContent, prefix: string, customOnly?: boolean): CustomView[] => {
  return Object.keys(obj)
    .filter(
      (key) =>
        key.startsWith(prefix) && (customOnly ? ((obj?.[key as PrefKey] || {}) as GridPreferences).customView : true)
    )
    .map((key) => ({ page: prefix, name: key.substring(prefix.length) }))
    .sort((a, b) => (a.name < b.name ? -1 : 1));
};

export const downloadBlob = (blob: Blob, fileName: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export const downloadCsvFromData = (csvData: string, nameFile: string) => {
  downloadBlob(new Blob([csvData], { type: 'text/csv;charset=utf-8;' }), nameFile);
};

export const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB'];
  let value = bytes / 1024;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex++;
  }
  return `${Math.round(value * 10) / 10} ${units[unitIndex]}`;
};

export const getFiltersFromSearchParams = (filterKeys: string[]): FilterableField[] => {
  let parsedFilters: FilterableField[] = [];

  const currentUrl = new URL(window.location.href);
  if (currentUrl.searchParams.has('filters')) {
    const filters = currentUrl.searchParams.get('filters') || '';
    try {
      const temp = filters ? JSON.parse(filters) : [];

      if (Array.isArray(temp)) {
        parsedFilters = temp.filter(
          (item) =>
            item &&
            typeof item === 'object' &&
            'field' in item &&
            'operator' in item &&
            'value' in item &&
            filterKeys.includes(item.field)
        );
      }
    } catch {
      parsedFilters = [];
    }
  }
  return parsedFilters;
};

export const clearFiltersFromSearchParams = () => {
  const currentUrl = new URL(window.location.href);
  if (currentUrl.searchParams.has('filters')) {
    currentUrl.searchParams.delete('filters');
    window.history.replaceState({}, '', currentUrl.toString());
  }
};

export const normalizeMultiline = (text: string): string => {
  if (!text) return text;

  return text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').replace(/\\n/g, '\n');
};
